import { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { SuccessIcon, Checkbox, InlineLoader } from 'cat-ecommerce-alloy';

import ContactDealerDisplay from '@app/components/common/Availability/Common/ContactDealerDisplay';
import GstTableHeaderDisclaimer from '@app/components/common/product/GstTableHeaderDisclaimer';

import { checkIsRiyal, priceDisplayFormat } from '@app/utils/paymentUtils';

import { FITMENT_CHECK, STATUS } from '@app/constants/commonConstants';
import {
  PCC_PDP_LUCID_TAX_DISCLAIMER,
  PCC_RELEASE_CURRENCY_SYMBOL_UPDATE_FOR_SAUDI_RIYAL
} from '@app/constants/featureFlags';

import styles from '../FrequentlyBoughtTogether.module.scss';
import { isRTLLang } from '@app/utils';

const FbtCard = ({
  partName,
  imageData,
  getFitmentResult,
  price,
  partNumber,
  handleCheckBox,
  loadingStatus
}) => {
  const [t] = useTranslation();
  const currency = useSelector(s => s.dealerCustomer?.currency);
  const isCatCorp = useSelector(s => s.common?.isCatCorp);
  const current = useSelector(s => s.products?.current);
  const fitmentPartNumber = useSelector(
    s => s.products?.byId?.[current]?.partNumber
  );
  const taxDisclaimerFeatureFlag = useSelector(
    s => s.featureFlag?.[PCC_PDP_LUCID_TAX_DISCLAIMER]
  );
  const taxMessageGSTByOrder = useSelector(s => s.orders?.taxDisclaimerMessage);

  const fitmentResult = getFitmentResult(fitmentPartNumber);

  const [selectedCheckbox, setSelectedCheckbox] = useState([]);

  const langId = useSelector(s => s.common?.langId);
  const isRTL = isRTLLang(langId);
  const currencySymbolFeatureFlag = useSelector(
    state =>
      state?.featureFlag?.[PCC_RELEASE_CURRENCY_SYMBOL_UPDATE_FOR_SAUDI_RIYAL]
  );
  const isRiyal = currencySymbolFeatureFlag && checkIsRiyal(currency);
  const handleCheckboxChange = event => {
    const value = event.target.value;
    const isChecked = event.currentTarget.checked;
    if (isChecked) {
      const updatedCheckBox = [...selectedCheckbox, value];
      setSelectedCheckbox(updatedCheckBox);
      handleCheckBox(event.target.id, isChecked);
    } else {
      const updatedCheckBox = selectedCheckbox.filter(v => v !== value);
      setSelectedCheckbox(updatedCheckBox);
      handleCheckBox(event.target.id, isChecked);
    }
  };

  const getPrice = () => {
    if (loadingStatus === STATUS.PENDING) {
      return <InlineLoader />;
    } else {
      return price ? (
        <>
          <p className="u-fs--14 mb-0">
            {isRiyal
              ? priceDisplayFormat({
                  price,
                  currency,
                  priceWrapperClasses: 'fw-bold px-1',
                  isRTL: isRTL,
                  size: 'sm'
                })
              : priceDisplayFormat({
                  price,
                  currency,
                  priceWrapperClasses: 'fw-bold'
                })}
          </p>

          {taxDisclaimerFeatureFlag && !!taxMessageGSTByOrder && (
            <p className="mb-0">
              <GstTableHeaderDisclaimer
                className="fw-normal"
                taxDisclaimerMessage={taxMessageGSTByOrder}
              />
            </p>
          )}
        </>
      ) : (
        !isCatCorp && <ContactDealerDisplay />
      );
    }
  };

  return (
    <div className="mx-2 mt-1 mb-2">
      {!isCatCorp && (
        <Checkbox
          id={partNumber}
          label={partName}
          value={partNumber}
          checked={price}
          disabled={!price}
          hideLabel
          onChange={handleCheckboxChange}
        />
      )}
      <div className="d-flex justify-content-center">
        <img
          className={cx(styles['itemImage'])}
          src={imageData.url}
          alt={imageData.title}
        />
      </div>
      <p className="m-0 my-2 u-fs--14">{partName}</p>
      {getPrice()}
      {fitmentResult === FITMENT_CHECK.FULL_CHECK && (
        <div className="d-flex w-100 mt-2">
          <SuccessIcon
            height={16}
            width={16}
            className="u-fill-icon--MediumGreen me-1"
          />
          <p className="u-color--DarkGray u-fs--12 mb-0">
            {t('PART_FACTORY_FIT')}
          </p>
        </div>
      )}
    </div>
  );
};

FbtCard.propTypes = {
  partName: PropTypes.string,
  partNumber: PropTypes.string,
  imageData: PropTypes.shape({
    url: PropTypes.string,
    title: PropTypes.string
  }),
  getFitmentResult: PropTypes.func,
  price: PropTypes.string,
  handleCheckBox: PropTypes.func,
  loadingStatus: PropTypes.string
};
export default FbtCard;
